import { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FAQ_HOME_PATH } from '../HomePage/Extras/FAQSection';
import classes from './HelpMenu.module.css';
import useCreateSupportChatRoom from '../../hooks/messages/useCreateSupportChatRoom';
import { notifyErrorToast } from '../../util/notifyActionError';

const ChatIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M1.5 16.5L3.2 12.5C2.2 11.2 1.6 9.7 1.6 8C1.6 3.9 5.1 0.5 9.3 0.5C13.5 0.5 17 3.9 17 8C17 12.1 13.5 15.5 9.3 15.5C7.9 15.5 6.6 15.2 5.4 14.6L1.5 16.5Z" stroke="#1F1F1F" strokeWidth="1.2" strokeLinejoin="round"/>
  </svg>
);

const EmailIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="16" viewBox="0 0 20 16" fill="none" aria-hidden="true">
    <rect x="1" y="1.5" width="18" height="13" rx="2" stroke="#1F1F1F" strokeWidth="1.2"/>
    <path d="M1.5 3L10 9.5L18.5 3" stroke="#1F1F1F" strokeWidth="1.2" strokeLinejoin="round"/>
  </svg>
);

const FaqIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <circle cx="10" cy="10" r="8.5" stroke="#1F1F1F" strokeWidth="1.2"/>
    <path d="M7.8 7.4C7.8 5.9 9.1 4.8 10.6 4.8C12.1 4.8 13.2 5.9 13.2 7.4C13.2 8.5 12.5 9.2 11.4 9.8C10.6 10.2 10.1 10.8 10.1 11.7V12.1" stroke="#1F1F1F" strokeWidth="1.2" strokeLinecap="round"/>
    <circle cx="10.1" cy="14.5" r="0.8" fill="#1F1F1F"/>
  </svg>
);

const DocumentIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="20" viewBox="0 0 18 20" fill="none" aria-hidden="true">
    <path d="M4 1.5H11.5L15.5 5.5V18.5C15.5 18.8 15.2 19 15 19H4C3.7 19 3.5 18.8 3.5 18.5V2C3.5 1.7 3.7 1.5 4 1.5Z" stroke="#1F1F1F" strokeWidth="1.2" strokeLinejoin="round"/>
    <path d="M11.5 1.5V5.5H15.5" stroke="#1F1F1F" strokeWidth="1.2" strokeLinejoin="round"/>
    <path d="M6.5 9H12.5M6.5 12H12.5M6.5 15H10" stroke="#1F1F1F" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

const ShieldIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="20" viewBox="0 0 18 20" fill="none" aria-hidden="true">
    <path d="M9 1.5L16 4.5V9.5C16 13.8 13 17.2 9 18.5C5 17.2 2 13.8 2 9.5V4.5L9 1.5Z" stroke="#1F1F1F" strokeWidth="1.2" strokeLinejoin="round"/>
  </svg>
);

const HelpMenuContent = ({ onItemClick }) => {
  const isAuthenticated = useSelector((state) => state?.auth?.isAuthenticated);
  const navigate = useNavigate();
  const { openSupportChat, loading: supportLoading } = useCreateSupportChatRoom();

  const handleClick = () => {
    onItemClick?.();
  };

  const handleSupportChatClick = async () => {
    onItemClick?.();
    if (!isAuthenticated) {
      navigate('/kontakt');
      return;
    }
    try {
      const chatRoom = await openSupportChat();
      navigate(`/messages/${chatRoom.id}`, {
        state: { supportChat: chatRoom },
      });
    } catch (err) {
      console.error('[HelpMenu] Support chat error:', err);
      notifyErrorToast('Der Support-Chat konnte nicht geöffnet werden. Bitte versuche es erneut.', {
        title: 'Support-Chat nicht verfügbar',
      });
    }
  };

  return (
    <div className={classes.menu}>
      <button
        className={classes.menuItem}
        onClick={handleSupportChatClick}
        disabled={supportLoading}
        style={{ opacity: supportLoading ? 0.6 : 1 }}
      >
        <ChatIcon />
        <span className="regularSmall">Chat-Support kontaktieren</span>
      </button>
      <Link to="/kontakt" className={classes.menuItem} onClick={handleClick}>
        <EmailIcon />
        <span className="regularSmall">Kontakt per E-Mail</span>
      </Link>
      <div className={classes.divider} />
      <Link to={FAQ_HOME_PATH} className={classes.menuItem} onClick={handleClick}>
        <FaqIcon />
        <span className="regularSmall">FAQs</span>
      </Link>
      <Link to="/agb" className={classes.menuItem} onClick={handleClick}>
        <DocumentIcon />
        <span className="regularSmall">AGBs</span>
      </Link>
      <Link to="/datenschutz" className={classes.menuItem} onClick={handleClick}>
        <ShieldIcon />
        <span className="regularSmall">Datenschutz</span>
      </Link>
    </div>
  );
};

const HelpMenu = ({
  variant = 'dropdown',
  alignToParent = false,
  scrollable = false,
  anchorRef,
  open = true,
  onClose,
  onItemClick,
  panelRef,
  onMouseEnter,
  onMouseLeave,
}) => {
  const [anchoredStyle, setAnchoredStyle] = useState(null);

  useLayoutEffect(() => {
    if (!open || !anchorRef?.current) {
      setAnchoredStyle(null);
      return undefined;
    }

    const updatePosition = () => {
      const rect = anchorRef.current.getBoundingClientRect();
      const top = Math.max(8, rect.top - 100);
      const maxHeight = Math.max(160, window.innerHeight - top - 16);
      setAnchoredStyle({
        top: `${top}px`,
        left: `${rect.left - 12}px`,
        maxHeight: scrollable ? `${maxHeight}px` : undefined,
      });
    };

    updatePosition();

    const scrollParent = anchorRef.current.closest('[data-profile-menu-scroll]');
    scrollParent?.addEventListener('scroll', updatePosition, { passive: true });
    window.addEventListener('resize', updatePosition);

    return () => {
      scrollParent?.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, anchorRef, scrollable]);

  useEffect(() => {
    if (variant !== 'sheet' || !open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [variant, open]);

  if (!open) return null;

  const handleItemClick = () => {
    onItemClick?.();
    onClose?.();
  };

  if (variant === 'sheet') {
    return createPortal(
      <div className={classes.sheetOverlay} onClick={onClose}>
        <div
          ref={panelRef}
          data-help-menu-panel
          className={classes.sheet}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-label="Hilfe"
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <HelpMenuContent onItemClick={handleItemClick} />
        </div>
      </div>,
      document.body
    );
  }

  const panelClassName = [
    classes.panel,
    anchorRef ? classes.sideAnchored : (
      alignToParent && variant === 'side' ? classes.sideAlignParent : classes[variant]
    ),
    scrollable ? classes.scrollable : '',
  ].filter(Boolean).join(' ');

  const panel = (
    <div
      ref={panelRef}
      data-help-menu-panel
      className={panelClassName}
      style={anchoredStyle ? { top: anchoredStyle.top, left: anchoredStyle.left } : undefined}
      role="menu"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <HelpMenuContent onItemClick={handleItemClick} />
    </div>
  );

  if (anchorRef) {
    return createPortal(panel, document.body);
  }

  return panel;
};

export default HelpMenu;
